import React, { useState } from 'react';
import { Search, X, ShoppingCart, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { tireAPI, orderAPI, memberAPI } from '../services/api';

export const TireSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (query.trim() === '') {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    // Search tires via API
    setIsLoading(true);
    try {
      const results = await tireAPI.search(query);
      setSearchResults(results);
      setShowResults(true);
    } catch (error) {
      console.error('Error searching tires:', error);
      toast.error('Failed to search tires. Please try again.');
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  const addToCart = async (tire) => {
    try {
      // Check if user is a member (from localStorage for now)
      const memberData = JSON.parse(localStorage.getItem('memberData') || 'null');
      
      // Create order
      const orderData = {
        member_email: memberData?.email || null,
        customer_name: memberData?.name || 'Guest',
        customer_email: memberData?.email || 'guest@example.com',
        customer_phone: memberData?.phone || '555-0000',
        items: [{
          tire_size: tire.size,
          category: tire.category,
          quantity: 1,
          unit_price: tire.price,
          subtotal: tire.price
        }]
      };
      
      const response = await orderAPI.create(orderData);
      
      toast.success('Order Placed Successfully!', {
        description: `Order #${response.order_number} - Total: $${response.total.toFixed(2)}${response.points_earned > 0 ? ` (+${response.points_earned} points)` : ''}`
      });
      
      // Update member data in localStorage if applicable
      if (memberData && response.points_earned > 0) {
        const updatedMember = await memberAPI.getByEmail(memberData.email);
        localStorage.setItem('memberData', JSON.stringify(updatedMember));
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to place order. Please try again.');
    }
  };

  const scrollToFullCatalog = () => {
    const element = document.getElementById('full-catalog');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Main Search Card */}
        <Card className="p-8 lg:p-12 bg-gradient-to-br from-red-50 via-white to-gray-50 shadow-2xl border-2 border-red-100">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 rounded-2xl mb-4">
              <Search className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3">
              Find Your Tire Size & Get Instant Pricing
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Enter your tire size below to see our competitive prices and availability
            </p>
          </div>
          
          <div className="relative max-w-3xl mx-auto">
            <div className="relative">
              <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Enter tire size (e.g., 205/55R16, 265/70R17, LT285/75R16)"
                className="w-full pl-16 pr-14 py-5 text-lg font-medium border-2 border-gray-300 rounded-xl focus:border-red-500 focus:ring-4 focus:ring-red-100 outline-none transition-all shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              )}
            </div>

            {/* Search Results */}
            {showResults && (
              <div className="mt-6">
                {searchResults.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600 font-semibold">
                        Found {searchResults.length} result(s) for "{searchQuery}"
                      </p>
                    </div>
                    <div className="grid gap-4 max-h-[500px] overflow-y-auto pr-2">
                      {searchResults.map((tire, index) => (
                        <div
                          key={index}
                          className="bg-white border-2 border-green-200 rounded-xl p-6 hover:shadow-xl transition-all hover:border-green-300"
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2 flex-wrap">
                                <h3 className="text-3xl font-bold text-gray-900">{tire.size}</h3>
                                <Badge
                                  variant={tire.stock === 'In Stock' ? 'default' : 'secondary'}
                                  className={tire.stock === 'In Stock' ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-yellow-100 text-yellow-700 border border-yellow-300'}
                                >
                                  {tire.stock}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 font-medium mb-4">{tire.category}</p>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                                  <span>Professional Installation Included</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                                  <span>Balancing & Disposal Included</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-center lg:text-right lg:ml-6 border-t lg:border-t-0 lg:border-l border-gray-200 pt-4 lg:pt-0 lg:pl-6">
                              <div className="mb-2">
                                <div className="text-5xl font-bold text-red-600">${tire.price}</div>
                                <div className="text-sm text-gray-500 mt-1">per tire</div>
                              </div>
                              <Button
                                onClick={() => addToCart(tire)}
                                size="lg"
                                className="w-full lg:w-auto bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transition-all"
                                disabled={tire.stock !== 'In Stock' && tire.stock !== 'Low Stock'}
                              >
                                <ShoppingCart className="w-5 h-5 mr-2" />
                                Order Now
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-10 text-center">
                    <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-700 font-semibold text-lg mb-2">
                      No tires found for "{searchQuery}"
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      Try a different size or browse our full catalog below
                    </p>
                    <Button
                      onClick={scrollToFullCatalog}
                      variant="outline"
                      className="border-2 border-gray-300 hover:border-red-600 hover:text-red-600"
                    >
                      Browse Full Catalog
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Popular Sizes */}
            {!showResults && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-700 mb-4 font-semibold text-center">
                  Popular tire sizes - Click to search:
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  {['185/65R15', '205/55R16', '225/65R17', '265/70R17', 'LT285/75R16', '235/45R18'].map((size) => (
                    <button
                      key={size}
                      onClick={() => handleSearch(size)}
                      className="px-5 py-3 bg-white border-2 border-gray-300 hover:border-red-600 hover:bg-red-50 hover:text-red-600 text-gray-700 rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow-md"
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Bottom CTA */}
          {!showResults && (
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-4">Can't find your size?</p>
              <Button
                onClick={scrollToFullCatalog}
                variant="outline"
                size="lg"
                className="border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-all"
              >
                View Full Tire Catalog
              </Button>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
};
